import { openProDialog, openStarterDialog, openTeamDialog } from "@/shared";
import { useAccountStore } from "@features/account-management";
import { useCallback } from "react";
import { track } from "@/features/analytics";
import { VARIABLES_EVENTS } from "../../analytics/variables-analytics";
import type { PlanTier } from "../types";

const ORDER: Record<PlanTier, number> = { starter: 0, pro: 1, team: 2 };

/**
 * Plan gating for variables. The current plan comes from the account store;
 * `trial` is treated as `starter`. A variable is locked when its required tier
 * is above the current tier.
 */
export const usePlanLock = () => {
  const planType = useAccountStore((s) => s.planType);
  const currentTier: PlanTier = planType === "trial" ? "starter" : planType;
  const currentOrder = ORDER[currentTier];

  // Referentially stable per plan tier so callers may list it in a
  // `useMemo`/`useCallback` dependency array (e.g. the inline typeahead's
  // available-before-locked ordering) without recomputing every render.
  const isPlanLocked = useCallback(
    (plan?: PlanTier) => plan !== undefined && ORDER[plan] > currentOrder,
    [currentOrder],
  );

  /** `name`: the locked variable (or variable type) that was clicked. */
  const openUpgrade = (plan: PlanTier, source: string, name?: string) => {
    track(VARIABLES_EVENTS.CLICK_LOCKED_VARIABLE, {
      "required plan": plan,
      surface: source,
      ...(name ? { "variable name": name } : {}),
    });
    if (plan === "starter") openStarterDialog(source);
    else if (plan === "pro") openProDialog(source);
    else openTeamDialog(source);
  };

  return { isPlanLocked, openUpgrade };
};
